import { API_ENDPOINT, BASE_ENDPOINT } from "../constants";
import axios from "axios";
import auth from "./auth";
import store from "../store";
import { updateUserDetail } from "../actions";
import jwtDecode from "jwt-decode";
import { appendApiMessage } from "../utils/apiError";

const apiClient = axios.create({
  withCredentials: true,
});

let isRefreshing = false;
let failedQueue: { resolve: (value?: unknown) => void; reject: (reason?: unknown) => void }[] = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      const requestUrl = String(originalRequest?.url || "");
      const onPublicClassPage = window.location.pathname.startsWith("/class/");
      if (
        onPublicClassPage ||
        requestUrl.includes("public/ss-classes") ||
        requestUrl.includes("ss-classes/students")
      ) {
        await appendApiMessage(error);
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers["Authorization"] = `Bearer ${token}`;
          return apiClient(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const res = await axios.post(
          BASE_ENDPOINT + "api/auth/refresh",
          {},
          { withCredentials: true }
        );
        const newToken = res.data.token;
        const decoded: any = jwtDecode(newToken);
        store.dispatch(
          updateUserDetail({
            role: decoded.roleName,
            userId: decoded.userId,
            token: newToken,
            accType: decoded.accType,
          })
        );
        originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
        processQueue(null, newToken);
        return apiClient(originalRequest);
      } catch (refreshError) {
        await appendApiMessage(refreshError);
        processQueue(refreshError, null);
        auth.logout(() => {
          window.location.href = "/";
        });
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    await appendApiMessage(error);
    return Promise.reject(error);
  }
);

class AppService {
  private async request(method, type, body?, id?) {
    let data, url;
    if (method === "post") {
      data = body;
      url = `${type}`;
    } else if (method === "put") {
      data = body;
      url = `${type}/${id}`;
    } else if (method === "delete") {
      url = `${type}/${id}`;
    } else if (method === "get") {
      url = id != null ? `${type}/${id}` : `${type}`;
    }

    const options: any = {
      method: method,
      data: data,
      url: API_ENDPOINT + `${url}`,
    };

    if (this.getToken() != null) {
      options["headers"] = { Authorization: `Bearer ${this.getToken()}` };
    }
    const response = await apiClient(options);
    return response;
  }

  public getToken() {
    return auth.getToken();
  }

  public submit(data, _url) {
    return this.request("post", _url, data);
  }

  public update(data, _url, id) {
    return this.request("put", _url, data, id);
  }

  public delete(id, _url) {
    return this.request("delete", _url, null, id);
  }

  public async get(id, _url) {
    const response = await this.request("get", _url, null, id);
    return this.mapResponse(response.data);
  }

  public upload(data, _url) {
    return this.request("post", _url, data);
  }

  public getAll(_url) {
    return this.request("get", _url);
  }

  public getAllWithData(_url, id) {
    return this.request("get", _url, null, id);
  }

  public async downloadFromStream(
    data,
    _url,
    method,
    responseType,
    forceToReceiveInBlobFormat
  ) {
    const response = await apiClient(API_ENDPOINT + `${_url}`, {
      method: method,
      headers: { Authorization: `Bearer ${this.getToken()}` },
      data: {
        ...data,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
      responseType: forceToReceiveInBlobFormat && responseType,
    });

    return response;
  }

  public async awsURL(data, _url, method, contentType) {
    const response = await apiClient(API_ENDPOINT + `${_url}`, {
      method: method,
      headers: {
        Authorization: `Bearer ${this.getToken()}`,
        "Content-type": contentType,
      },
      data: data,
    });

    return response;
  }

  private mapResponse(object) {
    /**
     * This will handle if we receive type other than the object
     */
    if (typeof object !== "object") {
      return object;
    }

    var newMap = {};
    for (var i in object) newMap[i] = object[i];
    return newMap;
  }
}

export default new AppService();
